import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import {
    TextField,
    withStyles,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Chip,
    Paper, Grid
} from "@material-ui/core";
import * as Yup from "yup";
import { BackButton, hasPermission } from "../../sc-utilities";
import { Autocomplete } from "@material-ui/lab";

const styles = theme => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: "100%",
        marginTop: "10px"
    },
    formControl: {
        width: "100%"
    }
});

const DeviceCaseSupportedForm = props => {
    const { permissions, classes, formSettings, initialValues, handleSubmit, translations, isEdit, activeCustomerID, onFetchDiagnoseCases, onFetchDeviceModels } = props;

    const [diagnoseCases, setDiagnoseCases] = useState([]);
    const [deviceModels, setDeviceModels] = useState([]);

    const getDiagnoseCases = async (ClientType) => {
        const result = await onFetchDiagnoseCases({ ActiveCustomerID: activeCustomerID, q: { Visible: 1, ClientType } });
        if (result) {
            setDiagnoseCases(result);
        }
    };

    const getDeviceModels = async (ClientType) => {
        const result = await onFetchDeviceModels({ formatForWEBUI: true, ActiveCustomerID: activeCustomerID, q: { ClientType } });
        if (result) {
            setDeviceModels(result);
        }
    };

    useEffect(() => {
        getDiagnoseCases(initialValues?.ClientType);
        getDeviceModels(initialValues?.ClientType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Paper className={classes.root}>
            {BackButton(translations)}
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                onSubmit={handleSubmit}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {formikProps => {
                    const { values, touched, errors, setFieldValue, isValid, dirty, isSubmitting, handleChange, handleBlur, handleSubmit, setValues } = formikProps;

                    return (
                        <form onSubmit={handleSubmit}>
                            <Typography variant={"h6"}>{formSettings.FormTitle ? formSettings.FormTitle : null}</Typography>
                            <TextField
                                name="Name"
                                value={values.Name || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Name && touched.Name)}
                                placeholder={translations['device_case_supported_name'] || 'Device case supported name'}
                                label={translations['device_case_supported_name'] || 'Device case supported name'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <FormControl id="client_type" className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["choose_client_type"] || "Choose client type"}</InputLabel>
                                <Select
                                    name="ClientType"
                                    value={values.ClientType}
                                    onChange={(e) => { getDiagnoseCases(e.target.value); getDeviceModels(e.target.value); setValues({ ...values, ClientType: e.target.value, DiagnoseCases: [], DeviceModels: [] }) }}
                                    onBlur={handleBlur}
                                    error={!!(errors.ClientType && touched.ClientType)}
                                    margin={"dense"}
                                    fullWidth={true}
                                >
                                    <MenuItem value={'mobile'}>
                                        {translations['mobile'] || 'Mobile'}
                                    </MenuItem>
                                    <MenuItem value={'work'}>
                                        {translations['work'] || 'Work'}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <Grid container direction="row" justify="center" alignItems="center" spacing={5}>
                                <Grid item xs={12} sm={10}>
                                    <Autocomplete
                                        name="DiagnoseCases"
                                        multiple
                                        id="diagnose_cases"
                                        options={diagnoseCases}
                                        getOptionLabel={(option) => option.OriginalLongName || []}
                                        filterSelectedOptions
                                        getOptionSelected={(option, value) => option.DiagnoseCaseID === value.DiagnoseCaseID}
                                        value={values.DiagnoseCases || []}
                                        onChange={(_, value) => setFieldValue('DiagnoseCases', value)}
                                        onBlur={handleBlur}
                                        renderInput={(params) => (
                                            <TextField {...params} variant="standard" fullWidth={true} label={`${translations["choose_diagnose_cases"] || "Choose diagnose cases"}`} />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip label={option.OriginalLongName} {...getTagProps({ index })} />
                                            ))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    {values.DiagnoseCases?.length !== diagnoseCases?.length && <Button variant="contained" onClick={() => setFieldValue('DiagnoseCases', diagnoseCases)}>{translations["select_all"] || "Select all"}</Button>}
                                </Grid>
                            </Grid>
                            <Autocomplete
                                name="DeviceModels"
                                multiple
                                id="device_models"
                                options={deviceModels}
                                getOptionLabel={(option) => option.displayValue}
                                filterSelectedOptions
                                getOptionSelected={(option, value) => option.value === value.value}
                                value={values.DeviceModels}
                                onChange={(_, value) => setFieldValue('DeviceModels', value)}
                                onBlur={handleBlur}
                                renderInput={(params) => (
                                    <TextField {...params} variant="standard" fullWidth={true} label={`${translations["choose_device_models"] || "Choose device models"}`} />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip label={option.displayValue} {...getTagProps({ index })} />
                                    ))
                                }
                                disableCloseOnSelect
                            />
                            <div>
                                {formSettings.ButtonSubmit ? (
                                    <Button
                                        fullWidth={true}
                                        className={classes.submit}
                                        variant="contained"
                                        color={formSettings.ButtonSubmit.Color}
                                        type="submit"
                                        style={{ color: isSubmitting || !(isValid && dirty) ? null : 'white' }}
                                        disabled={isSubmitting || !(isValid && dirty)}
                                    >
                                        {isSubmitting ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </Paper>
    );
};

export default withStyles(styles, { withTheme: true })(DeviceCaseSupportedForm);
